
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle, Clock, Eye, MapPin, User, Zap, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { AlertDetailDialog } from './AlertDetailDialog';
import { useUserRoles } from '@/hooks/useUserRoles';
import { useClientTeams } from '@/hooks/useClientTeams';
import { useSupabaseUser } from '@/hooks/use-supabase-user';
import { toast } from 'sonner';

interface EmergencyReport {
  id: string;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'pending' | 'investigating' | 'resolved';
  guard_name: string;
  location: string;
  created_at: string;
  team_id: string;
}

export const EmergencyReportsList = () => {
  const [selectedReport, setSelectedReport] = useState<EmergencyReport | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const { isAdmin } = useUserRoles();
  const { teams: clientTeams, isClient } = useClientTeams();
  const { user } = useSupabaseUser();

  const { data: reports, isLoading, refetch } = useQuery({
    queryKey: ['emergency-reports-all', user?.id, isAdmin, clientTeams],
    queryFn: async () => {
      if (!user?.id) return [];

      // For admins, get guards from their admin group
      let guardIdsFilter: string[] | null = null;
      
      if (isAdmin) {
        // First, get all members of the admin group (parent + contributors)
        const { data: adminGroupMembers } = await supabase
          .from('admin_team')
          .select('member_id')
          .eq('admin_id', user.id)
          .in('member_type', ['parent_admin', 'contributor']);
        
        if (adminGroupMembers && adminGroupMembers.length > 0) {
          const adminIds = adminGroupMembers.map(m => m.member_id);
          
          // Now get all guards created by these admins
          const { data: guards } = await supabase
            .from('profiles')
            .select('id')
            .in('created_by', adminIds);
          
          if (guards && guards.length > 0) {
            guardIdsFilter = guards.map(g => g.id);
          } else {
            // No guards found, return empty
            return [];
          }
        } else {
          // No admin group found, return empty
          return [];
        }
      }

      let query = supabase
        .from('emergency_reports')
        .select('*')
        .order('created_at', { ascending: false});

      // Apply filters based on role
      if (isAdmin && guardIdsFilter) {
        // Admins see reports from guards in their admin group
        query = query.in('guard_id', guardIdsFilter);
      } else if (!isAdmin) {
        // Non-admins filter by team access
        const userTeamIds = [...clientTeams];
        
        // Also get teams where user is a team member
        const { data: teamMembers } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('profile_id', user.id);
          
        if (teamMembers) {
          userTeamIds.push(...teamMembers.map(tm => tm.team_id));
        }
        
        // Get teams where user is a manager
        const { data: managerTeams } = await supabase
          .from('manager_teams')
          .select('team_id')
          .eq('manager_id', user.id);
          
        if (managerTeams) {
          userTeamIds.push(...managerTeams.map(mt => mt.team_id));
        }
        
        // Remove duplicates
        const uniqueTeamIds = [...new Set(userTeamIds)];
        
        if (uniqueTeamIds.length > 0) {
          query = query.in('team_id', uniqueTeamIds);
        } else {
          // If user has no team access, return empty array
          return [];
        }
      }

      query = query.limit(50);
      const { data, error } = await query;

      if (error) throw error;
      
      // Get guard details separately
      if (data && data.length > 0) {
        const guardIds = [...new Set(data.map(report => report.guard_id).filter(Boolean))];
        if (guardIds.length > 0) {
          const { data: profiles } = await supabase
            .from('profiles')
            .select('id, full_name, first_name, last_name')
            .in('id', guardIds);
          
          // Merge guard data
          return data.map(report => ({
            ...report,
            profiles: profiles?.find(p => p.id === report.guard_id) || null
          })) as EmergencyReport[];
        }
      }
      
      return data as EmergencyReport[];
    },
    enabled: !!user?.id
  });

  const handleReportClick = (report: EmergencyReport) => {
    setSelectedReport(report);
    setIsDetailOpen(true);
  };

  const handleUpdate = () => {
    refetch();
  };

  const handleDelete = async (reportId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this emergency report?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('emergency_reports')
        .delete()
        .eq('id', reportId);

      if (error) throw error;
      
      toast.success('Emergency report deleted successfully');
      refetch();
    } catch (error) {
      console.error('Error deleting emergency report:', error);
      toast.error('Failed to delete emergency report');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-50 text-red-600 border-red-100';
      case 'high':
        return 'bg-orange-50 text-orange-600 border-orange-100';
      case 'medium':
        return 'bg-amber-50 text-amber-600 border-amber-100';
      case 'low':
        return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved':
        return 'bg-emerald-50 text-emerald-600 border-emerald-100';
      case 'investigating':
        return 'bg-blue-50 text-blue-600 border-blue-100';
      case 'pending':
        return 'bg-amber-50 text-amber-600 border-amber-100';
      default:
        return 'bg-slate-50 text-slate-600 border-slate-100';
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
          <p className="text-lg font-medium text-muted-foreground">Loading emergency reports...</p>
        </div>
      </div>
    );
  }

  if (!reports?.length) {
    return (
      <Card className="bg-gradient-to-br from-muted/30 to-muted/50 border-red-200/50">
        <CardContent className="text-center py-16">
          <div className="flex flex-col items-center space-y-6">
            <div className="p-6 bg-gradient-to-br from-red-100 to-red-200 rounded-2xl">
              <AlertTriangle className="h-16 w-16 text-red-500" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-muted-foreground">No Emergency Reports</h3>
              <p className="text-muted-foreground/70">All clear! No emergency reports found.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {reports.map((report, index) => (
          <Card 
            key={report.id} 
            className={cn(
              "group cursor-pointer transform transition-all duration-300 hover:scale-[1.02] hover:shadow-luxury",
              "bg-gradient-to-br from-background/95 via-background/90 to-red-50/50 border-red-200/50",
              "hover:from-background hover:to-background hover:border-red-300/70 dark:hover:from-card dark:hover:to-card dark:hover:border-red-400/50",
              "backdrop-blur-sm"
            )}
            onClick={() => handleReportClick(report)}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="relative overflow-hidden">
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/5 to-orange-500/5 group-hover:from-red-500/15 group-hover:to-orange-500/15 dark:group-hover:from-red-500/20 dark:group-hover:to-orange-500/20 transition-all duration-300"></div>
              
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
              
              <div className="relative p-6 space-y-4">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "p-2 rounded-xl transition-all duration-300 group-hover:scale-110",
                      report.severity === 'critical' ? 'bg-red-500 text-white' :
                      report.severity === 'high' ? 'bg-orange-500 text-white' :
                      report.severity === 'medium' ? 'bg-amber-500 text-white' :
                      'bg-emerald-500 text-white'
                    )}>
                      {report.severity === 'critical' && <Zap className="h-4 w-4" />}
                      {report.severity === 'high' && <AlertTriangle className="h-4 w-4" />}
                      {report.severity === 'medium' && <AlertTriangle className="h-4 w-4" />}
                      {report.severity === 'low' && <AlertTriangle className="h-4 w-4" />}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-foreground group-hover:text-red-700 dark:group-hover:text-red-300 transition-colors line-clamp-1">
                        {report.title}
                      </h3>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <Badge className={`${getSeverityColor(report.severity)} text-xs border font-medium`}>
                      {report.severity}
                    </Badge>
                    <Badge className={`${getStatusColor(report.status)} text-xs border font-medium`}>
                      {report.status}
                    </Badge>
                  </div>
                </div>
                
                {/* Description */}
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 group-hover:text-foreground/90 dark:group-hover:text-muted-foreground transition-colors">
                  {report.description || 'No description available'}
                </p>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                    <MapPin className="h-3 w-3 text-primary" />
                    <span className="text-xs font-medium truncate">{report.location || 'No location'}</span>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                    <Clock className="h-3 w-3 text-primary" />
                    <span className="text-xs font-medium">{format(new Date(report.created_at), 'MMM d, HH:mm')}</span>
                  </div>
                </div>
                
                {/* Footer */}
                <div className="flex items-center justify-between pt-3 border-t border-red-200/50">
                  <div className="flex items-center gap-2">
                    <User className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">
                      {report.guard_name || 
                       (report as any).profiles?.full_name || 
                       `${(report as any).profiles?.first_name || ''} ${(report as any).profiles?.last_name || ''}`.trim() || 
                       'Unknown Guard'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    {report.status === 'resolved' ? (
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                    ) : report.status === 'investigating' ? (
                      <Eye className="h-4 w-4 text-blue-500" />
                    ) : (
                      <Clock className="h-4 w-4 text-amber-500" />
                    )}
                  </div>
                </div>
                
                {/* Admin delete button */}
                {isAdmin && (
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={(e) => handleDelete(report.id, e)}
                      className="h-8 w-8 p-0 bg-red-500 hover:bg-red-600"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                
                {/* Floating action indicator */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1">
                  <div className="w-8 h-8 border-2 border-red-500 rounded-full flex items-center justify-center bg-red-500/10 backdrop-blur-sm">
                    <div className="w-3 h-3 border-t-2 border-r-2 border-red-500 rotate-45"></div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <AlertDetailDialog
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        alert={selectedReport}
        type="emergency"
        onUpdate={handleUpdate}
      />
    </>
  );
};
